import { z } from "zod";
export const PageQuery = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(120).default(40),
  q: z.string().max(100).default(""),
});
export const VocabWord = z.object({
  id: z.uuid(),
  word: z.string(),
  pos: z.string().default(""),
  ipa: z.string().nullable(),
  meaning: z.string(),
  example: z.string().nullable(),
  exampleKind: z.enum(["source", "template"]).default("source"),
  collocation: z.string().nullable().default(null),
  cefr: z.string().nullable().default(null),
  topicId: z.uuid().nullable().default(null),
  saved: z.boolean().default(false),
  due: z.boolean().default(false),
  learning: z.boolean().default(false),
  reps: z.int().default(0),
});
export type VocabWord = z.infer<typeof VocabWord>;
export const WordInput = z.strictObject({
  word: z.string().trim().min(1).max(100),
  wordId: z.uuid().nullable().default(null),
  ipa: z.string().max(200).nullable().default(null),
  meaning: z.string().max(3000).nullable().default(null),
  example: z.string().max(4000).nullable().default(null),
  colloc: z.string().max(2000).nullable().default(null),
  review: z.boolean().default(true),
});
export const Lesson = z.object({
  key: z.string(),
  title: z.string(),
  groupKey: z.string(),
  html: z.string().optional(),
  sort: z.int(),
  total: z.int(),
  done: z.int(),
  wrong: z.int(),
  studyRefId: z.uuid(),
  locked: z.boolean(),
});
export type Lesson = z.infer<typeof Lesson>;
export const Exercise = z.object({
  id: z.uuid(),
  key: z.string(),
  part: z.int(),
  sort: z.int(),
  prompt: z.string(),
  passageHtml: z.string().nullable(),
  audioKey: z.string().nullable().default(null),
  imageKeys: z.array(z.string()).default([]),
  studyRefType: z.enum(["remedy", "wrong"]).default("remedy"),
  options: z.array(z.object({ key: z.enum(["A", "B", "C", "D"]), text: z.string() })),
  done: z.boolean(),
  correct: z.enum(["A", "B", "C", "D"]).optional(),
  explanation: z.unknown().optional(),
  translation: z.unknown().optional(),
});
export type Exercise = z.infer<typeof Exercise>;
export const GradeExercise = z.strictObject({ id: z.uuid(), chosen: z.enum(["A", "B", "C", "D"]) });
export const Topic = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  nameEn: z.string().nullable(),
  icon: z.string().nullable(),
  sort: z.int(),
  count: z.int(),
  locked: z.boolean(),
});
export type Topic = z.infer<typeof Topic>;
export const RevisionStats = z.object({
  total: z.int(),
  correct: z.int(),
  open: z.int(),
  groups: z.int(),
  skills: z.array(
    z.object({ key: z.string(), title: z.string(), part: z.int(), wrong: z.int(), total: z.int() }),
  ),
  traps: z.array(z.object({ key: z.string(), title: z.string(), wrong: z.int(), total: z.int() })),
  parts: z.array(
    z.object({
      part: z.int(),
      total: z.int(),
      correct: z.int(),
      poolTotal: z.int(),
      poolDone: z.int(),
      target: z.int(),
    }),
  ),
  priorityPart: z.int(),
  due: z.int(),
});
export type RevisionStats = z.infer<typeof RevisionStats>;
export const SrsCard = z.object({
  id: z.uuid(),
  word: VocabWord,
  reps: z.int(),
  dueDate: z.string(),
  lastReviewedAt: z.string().nullable(),
});
export type SrsCard = z.infer<typeof SrsCard>;

export type WordInput = z.infer<typeof WordInput>;

/** Exact sentence frames from the final legacy vocabulary renderer. */
export function legacyVocabExample(word: string, pos: string, meaning: string) {
  const form = pos.toLowerCase();
  if (/adv/.test(form)) return `The staff handled the customer request ${word}.`;
  if (/adj/.test(form)) return `The new proposal looks quite ${word} to everyone on the team.`;
  if (/^v|verb/.test(form)) return `Our team will ${word} the new schedule before the deadline.`;
  if (/^n|noun/.test(form)) return `The manager asked about the ${word} during the meeting.`;
  return `“${word}” là từ hay gặp trong đề TOEIC — nghĩa: ${meaning}.`;
}
